import { OrderDetail as ApiOrderDetail } from "randmar-api-client";
import React, { useMemo } from "react";
import { FormattedNumber } from 'react-intl';
import { Link } from 'react-router-dom';
import { Card } from 'randmarcomps';
import CurrencyUtils from '../../components/util/CurrencyUtils';
import { useManufacturer } from '../../contexts/WithManufacturer';
import Path from '../../routes/Path';
import { OrderDetail as LegacyOrderDetail } from '../../stores/document/Document';

interface IProps {
  openOrders?: (LegacyOrderDetail | ApiOrderDetail)[];
  navLink?: string;
}

type NormalizedOrderDetail = {
  documentType?: string | null;
  warehouseCode?: string | null;
  extendedPrice?: number | undefined;
}

const isLegacyOrderDetail = (order: LegacyOrderDetail | ApiOrderDetail): order is LegacyOrderDetail => 'documentType' in order;

const normalizeOrderDetail = (order: LegacyOrderDetail | ApiOrderDetail): NormalizedOrderDetail => {
  if (isLegacyOrderDetail(order)) {
    return {
      documentType: order.documentType,
      warehouseCode: order.warehouseCode,
      extendedPrice: order.extendedPrice
    };
  }
  return {
    documentType: order.DocumentType,
    warehouseCode: order.WarehouseCode,
    extendedPrice: order.ExtendedPrice
  };
};

const OpenOrdersSummary: React.FC<IProps> = ({ openOrders, navLink }) => {
  const { applicationId } = useManufacturer();

  const normalizedOpenOrders = useMemo(() => {
    return openOrders?.map(normalizeOrderDetail) || [];
  }, [openOrders]);

  const orders = normalizedOpenOrders.filter(o => o.documentType === "Order");
  const shipments = normalizedOpenOrders.filter(o => o.documentType === "Shipment");

  const currentOrder = orders.filter(o => o.warehouseCode !== "3PLE");
  const currentOrderCount = currentOrder.length;
  const currentOrderAmount = currentOrder.map(o => o.extendedPrice || 0).reduce((prev, next) => prev + next, 0);

  const delayedOrder = orders.filter(o => o.warehouseCode === "3PLE");
  const delayedOrderCount = delayedOrder.length;
  const delayedOrderAmount = delayedOrder.map(o => o.extendedPrice || 0).reduce((prev, next) => prev + next, 0);

  const shipmentCount = shipments!.length;
  const shipmentAmount = shipments.map(s => s.extendedPrice || 0).reduce((prev, next) => prev + next, 0);

  return (
    <Card className="w-full overflow-hidden">
      <div className="flex flex-col md:flex-row">
        <Link to={navLink || Path.orderDetails(applicationId)} className="flex-1 bg-blue-50 hover:bg-blue-100 p-4 transition-colors text-center">
          <p className="text-sm font-semibold text-gray-600">Processing</p>
          <div className="flex items-center justify-center space-x-4 mt-2">
            <p className="text-3xl font-bold text-gray-800">{currentOrderCount}</p>
            <div className="h-8 border-l border-gray-300"></div>
            <p className="text-3xl font-bold text-gray-800"><FormattedNumber value={currentOrderAmount} {...CurrencyUtils.format} /></p>
          </div>
        </Link>
        <div className="border-b md:border-b-0 md:border-l border-gray-200" />
        <Link to={navLink || Path.orderDetails(applicationId)} className="flex-1 bg-green-50 hover:bg-green-100 p-4 transition-colors text-center">
          <p className="text-sm font-semibold text-gray-600">Shipped</p>
          <div className="flex items-center justify-center space-x-4 mt-2">
            <p className="text-3xl font-bold text-gray-800">{shipmentCount}</p>
            <div className="h-8 border-l border-gray-300"></div>
            <p className="text-3xl font-bold text-gray-800"><FormattedNumber value={shipmentAmount} {...CurrencyUtils.format} /></p>
          </div>
        </Link>
        <div className="border-b md:border-b-0 md:border-l border-gray-200" />
        <Link to={navLink || Path.orderDetails(applicationId)} className="flex-1 bg-yellow-50 hover:bg-yellow-100 p-4 transition-colors text-center">
          <p className="text-sm font-semibold text-gray-600">Delayed</p>
          <div className="flex items-center justify-center space-x-4 mt-2">
            <p className="text-3xl font-bold text-gray-800">{delayedOrderCount}</p>
            <div className="h-8 border-l border-gray-300"></div>
            <p className="text-3xl font-bold text-gray-800"><FormattedNumber value={delayedOrderAmount} {...CurrencyUtils.format} /></p>
          </div>
        </Link>
      </div>
    </Card>
  );
};

export default OpenOrdersSummary;
